import { EventTypes } from '@event/types';
import { Alpha2CountryCode } from './common';

enum FOOD_PREFERENCE {
    NO_PREFERENCE = 'no_preference',
    VEGETARIAN = 'vegetarian',
    PESCETARIAN = 'pescetarian',
    NUT_FREE = 'nut_free',
    GLUTEN_LACTOSE_FREE = 'gluten_lactose_free',
    VEGAN = 'vegan',
}

interface IUser {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    company_name?: string;
    country_name?: string;
    country_id?: string;
    country?: string;
    address1?: string;
    address2?: string;
    mobile_phone?: string;
    telephone?: string;
    fax?: string;
    job_title?: 'other' | string;
    job_title_other?: string;
    city?: string;
    state?: string;
    email: string;
    food_preference?: FOOD_PREFERENCE;
    language: Alpha2CountryCode;
    last_login: string;
    date_joined: string;
    role: Roles;
    role_display: string;
    exact_debtor_number?: number;
    is_event_organizer?: boolean;
    keycloak_migration_date?: Date | null;
    uses_keycloak?: boolean;
    sales_manager?: number;
    is_eligible_for_extended_warranty?: boolean;
    manages_banners?: boolean;

    // Not yet available on the backend, implemented on the frontend for future avatar logic
    profile_picture?: string;
}

type AuthMeUser = Omit<IUser, 'full_name' | 'country_name'>;

type UserList = Pick<
    IUser,
    | 'id'
    | 'first_name'
    | 'last_name'
    | 'full_name'
    | 'company_name'
    | 'role'
    | 'role_display'
    | 'city'
    | 'state'
    | 'country'
    | 'sales_manager'
    | 'is_eligible_for_extended_warranty'
    | 'email'
>;

type ManagementAccountPermissionReason =
    | 'serializer_read_only'
    | 'exact_account'
    | 'victron_only'
    | 'victron_or_sales_director_only'
    | 'sales_management_account_only';

type ManagementAccountFieldPermission = {
    /** Whether the current requester may update this field for the account. */
    read_only: boolean;
    /** Backend reason codes that make the field read-only. */
    reasons: ManagementAccountPermissionReason[];
    /** Role choices accepted by the backend for the `role` field. */
    allowed_values?: Roles[];
    /** Roles for which the field is required. */
    required_for_roles?: Roles[];
    /** Conditional requirement; currently `job_title_other` when `job_title` is `other`. */
    required_when?: { job_title: 'other' };
    /** Whether the field is visible for the account; currently used by `managing_countries`. */
    visible?: boolean;
    required?: boolean;
};

/** The backend emits one permission entry for each serializer field, including this property. */
export type ManagementAccountFieldName = keyof ManagementAccountDetailFields | 'field_permissions';
export type ManagementAccountFieldPermissions = Record<ManagementAccountFieldName, ManagementAccountFieldPermission>;

export type ManagementAccountDetail = ManagementAccountDetailFields & {
    /** Per-field permissions and validation hints computed by the backend for this requester and account. */
    field_permissions: ManagementAccountFieldPermissions;
};

export type ManagementAccountCertificate = {
    event_id: number;
    event_type: EventTypes;
    event_type_slug: string;
    name: string;
    date_start: string;
    date_end: string;
    country_name: string;
    country_code: string;
    subscriber_id: number;
    certificate_download_url: string;
    dropbox_link: string;
};

type ManagementAccountDetailFields = Pick<
    IUser,
    | 'id'
    | 'role'
    | 'sales_manager'
    | 'first_name'
    | 'last_name'
    | 'full_name'
    | 'company_name'
    | 'telephone'
    | 'mobile_phone'
    | 'fax'
    | 'email'
    | 'is_eligible_for_extended_warranty'
    | 'country'
    | 'city'
    | 'state'
    | 'address1'
    | 'address2'
    | 'job_title'
    | 'job_title_other'
    | 'food_preference'
    | 'language'
> & {
    sales_director?: number;
    managing_countries?: boolean;
    distributor?: number;
    receive_mailings_targeted_at_professionals?: 0 | 1;
    country_region?: string;
    is_news_subscriber?: boolean;
};

type PublicUserInfo = Pick<
    IUser,
    'first_name' | 'last_name' | 'full_name' | 'email' | 'company_name' | 'country_id' | 'food_preference'
> & {
    language: string;
};

type UserAccountResponse = Pick<IUser, 'id' | 'first_name' | 'last_name'> & {
    company_name: string;
    country: {
        code: string;
        name: string;
    };
    address1: string;
    address2?: string;
    mobile_phone: string;
    city: string;
    state: string;
};

type UserAccountData = Omit<UserAccountResponse, 'country' | 'id' | 'state'> & { country_code: string };

enum Roles {
    VICTRON = 'victron',
    SALES_MANAGER = 'sales_manager',
    SALES_DIRECTOR = 'sales_director',
    DISTRIBUTOR = 'distributor',
    DEALER = 'dealer',
    END_USER = 'end_user',
    INSTALLER = 'installer',
    AUTHORIZED_INSTALLER = 'authorized_installer',
    UNREGISTERED = 'unregistered',
    AMBASSADOR = 'ambassador',
    DEFAULT = Roles.END_USER,
}

// Login form types
interface ILoginFormValues {
    email: string;
    password: string;
    showPasswordField: boolean;
}

interface ILoginResponse {
    user_id: number;
    email: string;
    full_name: string;
    role: Roles;
    sessionid: string;
}

interface ISignUpAPIFormValues {
    email: string;
    first_name: string;
    last_name: string;
    company_name?: string;
    requested_role?: Roles;
    country: string;
    language?: string;
    sales_manager?: number;
    distributor?: number;
    recaptcha_token: string;
}

interface ISignUpFormValues {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
    company: string;
    role: string;
    country: string;
    language: string;
}

interface ISetNewPasswordFormValues {
    new_password: string;
    confirm_new_password: string;
}

interface ISetNewPasswordData {
    password: string;
    password_hash: string;
}

enum KeycloakUserResponses {
    NO_USER_FOUND_PROFESSIONAL = 'no_user_found_professional',
    NO_USER_FOUND_KEYCLOAK = 'no_user_found_keycloak',
    DOES_NOT_USE_KEYCLOAK = 'does_not_use_keycloak',
    USES_KEYCLOAK = 'uses_keycloak',
    LEGACY_LOGIN = 'legacy_login',
    VERIFY_EMAIL = 'verify_email_required',
    DEACTIVATED = 'user_deactivated',
}

export type {
    AuthMeUser,
    ILoginFormValues,
    ILoginResponse,
    ISetNewPasswordData,
    ISetNewPasswordFormValues,
    ISignUpAPIFormValues,
    ISignUpFormValues,
    IUser,
    PublicUserInfo,
    UserAccountData,
    UserAccountResponse,
    UserList,
};

export { FOOD_PREFERENCE, KeycloakUserResponses, Roles };
